import React, {useCallback, useEffect, useState} from 'react';
import {equipementFiltersType} from "../../../pages/front/Equipement";
import {collection} from "../../../models/collection";
import {equipment} from "../../../models/equipment";
import {size} from "../../../models/size";
import {useForm, Controller} from "react-hook-form";
import {bodyArea} from "../../../models/bodyArea";

type FormDataType = {
  collection: string[],
  bodyArea: string[],
  size: string[],
}

type EquipementFiltreProps = {
  equipments: equipment[];
  setFilters: (f: equipementFiltersType) => void;
}

export const EquipementFiltre = ({equipments, setFilters}: EquipementFiltreProps) => {
  const [filterHidden, setFilterHidden] = useState(true);
  const [collections, initCollections] = useState([] as collection[]);
  const [sizes, initSizes] = useState([] as size[]);
  const [bodyAreas, initBodyAreas] = useState([] as bodyArea[]);

  const [bodyAreaSelected, setBodyArea] = useState([] as string[]);
  const [collectionSelected, setCollection] = useState([] as string[]);
  const [sizeSelected, setSize] = useState([] as string[]);

  const filters = useCallback(() => {
    setFilters({
      bodyArea: bodyAreaSelected,
      collection: collectionSelected,
      size: sizeSelected
    });
  }, [collectionSelected, sizeSelected, bodyAreaSelected, setFilters])

  useEffect(() => {
    filters();
  }, [filters]);

  useEffect(() => {
    const collections: collection[] = [];
    const sizes: size[] = [];
    const bodyAreas: bodyArea[] = [];

    equipments.forEach((equipment: equipment) => {
      if (-1 === collections.findIndex((collection: collection) => {
        return collection.collectionId === equipment.collection.collectionId;
      })) {
        collections.push(equipment.collection)
      }

      if (equipment.bodyArea) {
        if (-1 === bodyAreas.findIndex((bodyArea: bodyArea) => {
          return bodyArea.bodyAreaId === equipment.bodyArea?.bodyAreaId;
        })) {
          bodyAreas.push(equipment.bodyArea);
        }
      }

      equipment.sizes?.forEach((size: size) => {
        if (-1 === sizes.findIndex((s: size) => {
          return s.sizeId === size.sizeId;
        })) {
          sizes.push(size)
        }
      })
    });

    initCollections(collections.sort((a, b) => a.label.localeCompare(b.label)));
    initBodyAreas(bodyAreas.sort((a, b) => a.label.localeCompare(b.label)));
    initSizes(sizes.sort((a, b) => a.order - b.order));
  }, [equipments]);


  const {control, watch} = useForm<FormDataType>({
    defaultValues: {
      collection: [],
      bodyArea: [],
      size: [],
    },
  });

  const selectedCollections = watch('collection');
  const selectedBodyAreas = watch('bodyArea');
  const selectedSizes = watch('size');

  useEffect(() => {
    setCollection(selectedCollections)
  }, [selectedCollections]);

  useEffect(() => {
    setBodyArea(selectedBodyAreas)
  }, [selectedBodyAreas]);

  useEffect(() => {
    setSize(selectedSizes)
  }, [selectedSizes]);

  return (
    <>
      <div className="card card-body max-w905px m-auto">
        <div className="d-flex">
          <div className="col pe-2">
            {filterHidden && (
              <>
                {selectedSizes.length > 0 && (
                  <p className="d-inline me-4">
                    <strong className="me-1">Taille :</strong>
                    {selectedSizes.map((s: string, k: number) => (
                      <span key={k} className="badge bg-info bg-opacity-75 me-1">{s}</span>
                    ))}
                  </p>
                )}
                {selectedBodyAreas.length > 0 && (
                  <p className="d-inline me-4">
                    <strong className="me-1">Zone du corps :</strong>
                    {selectedBodyAreas.map((s: string, k: number) => (
                      <span key={k} className="badge bg-info bg-opacity-75 me-1">{s}</span>
                    ))}
                  </p>
                )}
                {selectedCollections.length > 0 && (
                  <p className="d-inline me-4">
                    <strong className="me-1">Collection :</strong>
                    {selectedCollections.map((s: string, k: number) => (
                      <span key={k} className="badge bg-info bg-opacity-75 me-1">{s}</span>
                    ))}
                  </p>
                )}
              </>
            )}
          </div>
          <div>
            <button className="btn btn-xs btn-primary" onClick={() => setFilterHidden(!filterHidden)}>
              {filterHidden
                ? (<><i className="fa-solid fa-magnifying-glass me-1"/>Filtrer</>)
                : (<><i className="fa-solid fa-chevron-up me-1"/>Masquer les filtres</>)
              }
            </button>
          </div>
        </div>
        {!filterHidden && (
          <form onSubmit={(e: React.FormEvent) => e.preventDefault()} className="mt-3">
            <div className="mb-3 d-flex">
              <strong className="me-2">Taille :</strong>
              <div className="col">
                {sizes.map((size: size, k: number) => (
                  <Controller
                    key={k}
                    name="size"
                    control={control}
                    render={({field}) => (
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={size.label}
                          checked={field.value.includes(size.label)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const value = e.target.value;
                            const newValues = checked
                              ? [...field.value, value]
                              : field.value.filter(v => v !== value);
                            field.onChange(newValues);
                          }}
                          id={`size-${size.label}`}
                        />
                        <label className="form-check-label" htmlFor={`size-${size.label}`}>
                          {size.label}
                        </label>
                      </div>
                    )}
                  />
                ))}
              </div>
            </div>
            <div className="mb-3 d-flex">
              <strong className="me-2">Zone du corps :</strong>
              <div className="col">
                {bodyAreas.map((bodyArea: bodyArea, k: number) => (
                  <Controller
                    key={k}
                    name="bodyArea"
                    control={control}
                    render={({field}) => (
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={bodyArea.label}
                          checked={field.value.includes(bodyArea.label)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const value = e.target.value;
                            const newValues = checked
                              ? [...field.value, value]
                              : field.value.filter(v => v !== value);
                            field.onChange(newValues);
                          }}
                          id={`bodyArea-${bodyArea.label}`}
                        />
                        <label className="form-check-label" htmlFor={`bodyArea-${bodyArea.label}`}>
                          {bodyArea.label}
                        </label>
                      </div>
                    )}
                  />
                ))}
              </div>
            </div>
            <div className="d-flex">
              <strong className="me-2">Collection :</strong>
              <div className="col">
                {collections.map((collection: collection, k: number) => (
                  <Controller
                    key={k}
                    name="collection"
                    control={control}
                    render={({field}) => (
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={collection.label}
                          checked={field.value.includes(collection.label)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const value = e.target.value;
                            const newValues = checked
                              ? [...field.value, value]
                              : field.value.filter(v => v !== value);
                            field.onChange(newValues);
                          }}
                          id={`collection-${collection.label}`}
                        />
                        <label className="form-check-label" htmlFor={`collection-${collection.label}`}>
                          {collection.label}
                        </label>
                      </div>
                    )}
                  />
                ))}
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
